import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, TrendingDown, Coins,
  Droplets, MessageSquare, AlertTriangle, Loader2,
} from 'lucide-react';
import { useCauldronTokenById, useCauldronPools } from '../hooks/useCauldronToken';
import { useNostrQuery } from '../hooks/useNostrQuery';
import {
  formatBch, formatPricePerToken,
} from '../lib/cauldron';
import PostCard from '../components/PostCard';
import SwapQuoteCard from '../components/SwapQuoteCard';

type Tab = 'social' | 'liquidity';

export default function TokenPage() {
  const { id } = useParams<{ id: string }>();
  const tokenId = (id || '').toLowerCase();
  const { token, loading } = useCauldronTokenById(tokenId);
  const [tab, setTab] = useState<Tab>('social');

  const up = token ? token.change24h >= 0 : true;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <Link to="/" className="shrink-0 rounded-full p-1.5 hover:bg-muted transition-colors" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            {token ? `$${token.symbol}` : 'Token'}
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        {loading && !token && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading token…
          </div>
        )}

        {!loading && !token && (
          <div className="glass-card !p-8 text-center">
            <Coins className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-bold">Token not found</p>
            <p className="text-sm text-muted-foreground mt-1">
              No CashToken with an active Cauldron pool matches this identifier.
            </p>
          </div>
        )}

        {token && (
          <>
            {/* Token hero */}
            <div className="glass-card !p-5">
              <div className="flex items-center gap-4">
                <TokenHeroIcon iconUrl={token.iconUrl} symbol={token.symbol} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xl font-bold">${token.symbol}</span>
                    {token.name && token.name.toLowerCase() !== token.symbol.toLowerCase() && (
                      <span className="text-sm text-muted-foreground truncate">{token.name}</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold">{formatPricePerToken(token.priceUsd)}</span>
                    {token.change24h !== 0 && (
                      <span className={`inline-flex items-center gap-0.5 text-sm font-semibold ${up ? 'text-emerald-500' : 'text-red-500'}`}>
                        {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {up ? '+' : ''}{token.change24h.toFixed(2)}%
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mt-5">
                <Stat label="Price (BCH)" value={token.priceBch > 0 ? token.priceBch.toExponential(2) : '—'} />
                <Stat label="TVL" value={formatBch(token.tvlBch)} />
                <Stat label="APY (30d)" value={token.apy > 0 ? `${token.apy.toFixed(1)}%` : '—'} />
              </div>

              {/* Trade CTA — live estimate + hand-off to Cauldron */}
              <div className="mt-5">
                <SwapQuoteCard
                  tokenId={token.tokenId}
                  symbol={token.symbol}
                  decimals={token.decimals}
                  priceUsd={token.priceUsd > 0 ? token.priceUsd : null}
                  iconUrl={token.iconUrl}
                  defaultUsd={5}
                />
              </div>
              <p className="text-[11px] text-muted-foreground text-center mt-2">
                Swaps execute on Cauldron DEX. BCHNostr never holds your funds.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mt-4 border-b border-border">
              <TabButton active={tab === 'social'} onClick={() => setTab('social')} icon={<MessageSquare className="w-4 h-4" />} label="Discussion" />
              <TabButton active={tab === 'liquidity'} onClick={() => setTab('liquidity')} icon={<Droplets className="w-4 h-4" />} label="Liquidity" />
            </div>

            <div className="mt-4">
              {tab === 'social'
                ? <TokenDiscussion symbol={token.symbol} tokenId={token.tokenId} />
                : <TokenLiquidity tokenId={token.tokenId} />}
            </div>
          </>
        )}

        <p className="text-[11px] text-muted-foreground text-center mt-8">
          Token data from the Cauldron / Riften Labs indexer.{' '}
          <a href="https://cauldron.quest" target="_blank" rel="noopener noreferrer" className="hover:underline">Learn more</a>.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted/50 p-3 text-center">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="font-bold text-sm mt-0.5 truncate">{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon} {label}
    </button>
  );
}

function TokenHeroIcon({ iconUrl, symbol }: { iconUrl: string; symbol: string }) {
  const [failed, setFailed] = useState(false);
  if (failed || !iconUrl) {
    return (
      <div className="shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
        <Coins className="w-8 h-8" />
      </div>
    );
  }
  return (
    <img
      src={iconUrl}
      alt={symbol}
      width={64}
      height={64}
      loading="lazy"
      className="shrink-0 w-16 h-16 rounded-full bg-muted object-cover"
      onError={() => setFailed(true)}
    />
  );
}

// ── Discussion tab: Nostr notes mentioning this token ────────────
function TokenDiscussion({ symbol, tokenId }: { symbol: string; tokenId: string }) {
  // NIP-50 content search on the ticker across read relays. We over-fetch and
  // filter client-side so we only show notes that actually mention $TICKER.
  const { events, loading } = useNostrQuery({
    filters: [{ kinds: [1], search: `$${symbol}`, limit: 60 }],
    enabled: !!symbol,
    allRelays: true,
  });

  const matched = useMemo(() => {
    const sym = symbol.toLowerCase();
    const re = new RegExp(`\\$${sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return events
      .filter(e => re.test(e.content || '') || (e.content || '').toLowerCase().includes(tokenId))
      .sort((a, b) => b.created_at - a.created_at);
  }, [events, symbol, tokenId]);

  if (loading && matched.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Finding posts about ${symbol}…
      </div>
    );
  }

  if (matched.length === 0) {
    return (
      <div className="glass-card !p-8 text-center">
        <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
        <p className="font-semibold text-sm">No posts yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Be the first to post about <span className="font-semibold text-primary">${symbol}</span> — just mention it in a note.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {matched.map(event => <PostCard key={event.id} event={event} />)}
    </div>
  );
}

// ── Liquidity tab: active Cauldron pools + IL warning ────────────
function TokenLiquidity({ tokenId }: { tokenId: string }) {
  const { pools, loading } = useCauldronPools(tokenId);
  const totalSats = pools.reduce((sum, p) => sum + p.sats, 0);

  return (
    <div className="space-y-3">
      {/* Impermanent-loss warning — ethical duty when pointing users at LPing */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
          Providing liquidity earns a 0.3% fee on swaps, but is <strong>not risk-free</strong>.
          If the token price moves, you can end up worth less than simply holding — this is
          called <strong>impermanent loss</strong>. Only provide liquidity you understand and can afford to lose.
        </p>
      </div>

      {loading && pools.length === 0 && (
        <div className="flex items-center justify-center py-10 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading pools…
        </div>
      )}

      {!loading && pools.length === 0 && (
        <div className="glass-card !p-8 text-center">
          <Droplets className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold text-sm">No active pools</p>
        </div>
      )}

      {pools.length > 0 && (
        <>
          <div className="glass-card !p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total liquidity</p>
              <p className="font-bold">{formatBch(totalSats / 1e8)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Active pools</p>
              <p className="font-bold">{pools.length}</p>
            </div>
          </div>

          <div className="space-y-2">
            {pools.slice(0, 20).map(pool => (
              <a
                key={pool.poolId || pool.txid}
                href={`https://explorer.bitcoinunlimited.info/tx/${pool.txid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card !p-3 flex items-center justify-between hover:border-primary/40 transition-all"
              >
                <div className="min-w-0">
                  <p className="text-xs font-mono text-muted-foreground truncate">{pool.ownerAddr || pool.ownerPkh}</p>
                </div>
                <span className="shrink-0 ml-3 text-sm font-semibold">{formatBch(pool.sats / 1e8)}</span>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
